import React from 'react';
import { CheckCircle2, AlertTriangle, ExternalLink, Ban } from 'lucide-react';
import { ALWAYS, AUTO, NEVER, modeOf, flagsFor, rulesFor } from '../lib/approvalMode';

// The leave type editor's approval choice and its rules panel (LAUNCH-CHECKLIST H9,
// 13/10/2026), plus the two switches that decide what other people see and count.
//
// ⚠️ ITS OWN FILE ON PURPOSE: SettingsPage.js is past 4,600 lines and CLAUDE.md says to
// stop adding to it. The logic (which mode, which rules apply) is in lib/approvalMode.js
// and pinned by its checks; this file only draws it.

const CHOICES = [
  { mode: ALWAYS, title: 'Always needs approval', hint: 'Every request goes to the approver' },
  { mode: AUTO, title: "Approved automatically when it's within the rules",
    hint: 'Anything outside them goes to the approver, as now' },
  { mode: NEVER, title: 'Never needs approval', hint: "It's recorded, not asked, like sick leave" },
];

// Opens in a new browser tab so nothing typed in this dialog is lost.
function ChangeLink({ tab, children }) {
  if (!tab) return null;
  return (
    <a
      href={`/settings?tab=${tab}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-xs underline text-primary whitespace-nowrap inline-flex items-center gap-1"
    >
      {children}
      <ExternalLink className="w-3 h-3" />
    </a>
  );
}

const TAB_NAMES = {
  limits: 'Leave limits',
  leave: 'Booking rules',
  managers: 'Manager permissions',
  blackouts: 'No-leave dates',
};

function RulesPanel({ form, limits, rules, managers, blackouts }) {
  const { checks, refuses, nothingChecked } = rulesFor({
    leaveType: form, limits, rules, managers, blackouts,
  });
  const name = (form.name || '').trim() || 'this leave type';
  return (
    <div className="rounded-lg bg-secondary/60 p-4 mt-2 ml-0 sm:ml-7 space-y-1" data-testid="auto-approval-rules">
      {nothingChecked ? (
        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 mb-2 text-sm"
             data-testid="auto-approval-nothing-checked">
          <p className="font-medium text-amber-700 dark:text-amber-400 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Nothing is checked yet
          </p>
          <p className="text-amber-700/90 dark:text-amber-400/90 mt-1">
            No rules apply to {name}, so every request for it would be approved automatically.
          </p>
        </div>
      ) : (
        <p className="text-sm mb-2">
          A request is approved straight away unless it breaks one of these. Breaking one sends
          it to the approver, with the reason shown.
        </p>
      )}
      {checks.map((c) => (
        <div key={c.key} className="flex items-start gap-3 py-2 border-t border-border first:border-t-0 text-sm">
          <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${c.applies ? 'text-emerald-600' : 'text-muted-foreground/50'}`} />
          <div className="flex-1 min-w-0">
            <p className={`font-medium ${c.applies ? '' : 'text-muted-foreground'}`}>{c.title}</p>
            <p className="text-xs text-muted-foreground">{c.value}</p>
          </div>
          <ChangeLink tab={c.tab}>{TAB_NAMES[c.tab]}</ChangeLink>
        </div>
      ))}
      <p className="text-xs uppercase tracking-wide text-muted-foreground pt-3">These refuse the booking anyway</p>
      {refuses.map((r) => (
        <div key={r.key} className="flex items-start gap-3 py-2 border-t border-border text-sm">
          <Ban className="w-4 h-4 mt-0.5 flex-shrink-0 text-muted-foreground/70" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-muted-foreground">{r.title}</p>
            {r.value && <p className="text-xs text-muted-foreground">{r.value}</p>}
          </div>
          <ChangeLink tab={r.tab}>{TAB_NAMES[r.tab]}</ChangeLink>
        </div>
      ))}
    </div>
  );
}

/** The three-way choice. Writes requires_approval and auto_approve together. */
export function ApprovalChoice({ form, setForm, limits, rules, managers, blackouts }) {
  const current = modeOf(form);
  return (
    <div data-testid="approval-choice">
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Approval</p>
      <div role="radiogroup" className="space-y-2">
        {CHOICES.map((c) => {
          const on = current === c.mode;
          return (
            <React.Fragment key={c.mode}>
              <label
                className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg border transition-colors ${
                  on ? 'border-primary ring-1 ring-primary' : 'border-border hover:bg-secondary/50'}`}
              >
                <input
                  type="radio"
                  name="approval-mode"
                  checked={on}
                  onChange={() => setForm({ ...form, ...flagsFor(c.mode) })}
                  className="w-4 h-4 mt-0.5 accent-primary"
                  data-testid={`approval-mode-${c.mode}`}
                />
                <div>
                  <p className="font-medium text-sm">{c.title}</p>
                  <p className="text-xs text-muted-foreground">{c.hint}</p>
                </div>
              </label>
              {on && c.mode === AUTO && (
                <RulesPanel form={form} limits={limits} rules={rules} managers={managers}
                            blackouts={blackouts} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/** "Counts towards people off at once" and "Private" — both were saved by this dialog
 *  but never shown in it, so every save reset them (BUGS.md, 13/10/2026). */
export function CountsAndPrivacy({ form, setForm }) {
  const name = (form.name || '').trim() || 'this type';
  return (
    <>
      <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-secondary/50 transition-colors">
        <input
          type="checkbox"
          checked={form.include_max_off !== false}
          onChange={(e) => setForm({ ...form, include_max_off: e.target.checked })}
          className="w-4 h-4 rounded border-input accent-primary"
          data-testid="leave-type-counts-as-off"
        />
        <div>
          <p className="font-medium text-sm">Counts towards "people off at once"</p>
          <p className="text-xs text-muted-foreground">
            Untick for things that aren't really time off, like working from home. Settings → Leave limits then ignores it.
          </p>
        </div>
      </label>
      <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-secondary/50 transition-colors">
        <input
          type="checkbox"
          checked={!!form.is_private}
          onChange={(e) => setForm({ ...form, is_private: e.target.checked })}
          className="w-4 h-4 rounded border-input accent-primary"
          data-testid="leave-type-private"
        />
        <div>
          <p className="font-medium text-sm">Private</p>
          <p className="text-xs text-muted-foreground">
            Colleagues see "Time off" instead of "{name}". Admins, and managers allowed to see leave
            reasons, still see the name. Connected calendars and the who's-off posts always say "Time off".
          </p>
        </div>
      </label>
    </>
  );
}
